import React, { useState, useMemo } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";  
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase";
import { serverTimestamp, doc, setDoc } from "firebase/firestore";
import { useToast } from "../ui/use-toast";
// If using react-hot-toast: import toast from "react-hot-toast";
// If using react-toastify: import { toast } from "react-toastify";

const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

export default function AddWhitelistEmails() {
    const { currentUser } = useAuth();
    const [rawEmailInput, setRawEmailInput] = useState("")
    const [toAdd, setToAdd] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { toast } = useToast();
    
    const emailList = useMemo(() => {
        if (!rawEmailInput) {
            return []
        }
        const result = rawEmailInput.match(emailPattern);
        return [...new Set(result)] ?? []
    }, [rawEmailInput])
    
    const handleAddAll = () => {
        setToAdd(emailList);
    }
    
    const handleClearAll = () => {
        setToAdd([])
    }
    
    const toggleItemSelect = (item) => {
        const index = toAdd.indexOf(item)
        if (index > -1) {
            setToAdd(toAdd.filter(email => email !== item))
        } else {
            setToAdd([...toAdd, item])
        }
    }
    
    const addItemToWhitelist = async (item) => {
        try {
            const docRef = doc(db, "whitelist", item);

            const data = {
                "email": item,
                "createdAt": serverTimestamp(),
                "addedBy": currentUser.email
            }

            await setDoc(docRef, data);
            console.log("Document successfully written with ID: ", item);
        } catch (e) {
            console.error("Error adding document: ", e);
            throw e; // Re-throw to handle in addEmails
        }
    };

    const addEmails = async () => {
        if (toAdd.length === 0) {
                  toast({
                    title: "Error",
                    description: "Please select an email first",
                    variant: "destructive",
                    duration: 5000,
                });
            return;
        }

        setIsSubmitting(true);
        
        try {
            const promises = []
            for (const item of toAdd) {
                promises.push(addItemToWhitelist(item))
            }
            
            await Promise.all(promises);
            
            // Success handling
            toast({
                    title: "Success",
                    description: promises.length + " emails have been added",
                    duration: 5000,
            });
            
            // Clear state
            setRawEmailInput("");
            setToAdd([]);
            
        } catch (error) {
            console.error("Error adding emails to whitelist:", error);
            
            // Specific error messages based on error type
            if (error.code === 'permission-denied') {
                toast({
                    title: "Error",
                    description: error.message || "There was an issue completing this request",
                    variant: "destructive",
                    duration: 5000,
                });
            } else if (error.code === 'unavailable') {
                toast({
                    title: "Error",
                    description: error.message || "There was an issue completing this request",
                    variant: "destructive",
                    duration: 5000,
                });
            } else {
                toast({
                    title: "Error",
                    description: error.message || "There was an issue completing this request",
                    variant: "destructive",
                    duration: 5000,
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    }
    
    const isSelected = (item) => toAdd.includes(item);
    
    return <Card>
        <CardContent>
            <div className="text-xl font-semibold mt-4">Whitelist Emails</div>
            <div className="text-gray-500 my-1">Input new emails to whitelist to the site here. You can input one or many!</div>
            <input
                value={rawEmailInput}
                onChange={(e) => setRawEmailInput(e.target.value)}
                className="border border-gray-300 rounded-md w-full px-3 py-2"
                placeholder="Input an email or emails"
                disabled={isSubmitting}
            />
            {emailList.length > 0 && 
                <div className="flex justify-between mt-2 items-center">
                    <div className="flex font-semibold">
                        {emailList.length} email{emailList.length > 1 && "s"} found ({toAdd.length} selected)
                    </div>    
                    <div className="flex gap-2">
                        <Button 
                            variant="outline" 
                            onClick={handleAddAll}
                            disabled={isSubmitting}
                        >
                            Add All
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={handleClearAll}
                            disabled={isSubmitting}
                        >
                            Clear All
                        </Button>
                    </div>
                </div>
            }
            <div className="mt-3 space-y-2">
                {emailList.map((item) => { 
                    const selected = isSelected(item);
                    return (
                        <div 
                            key={item} 
                            onClick={() => !isSubmitting && toggleItemSelect(item)}
                            className={`
                                px-4 py-2 rounded-md transition-colors
                                ${isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                                ${selected 
                                    ? 'bg-blue-100 border-2 border-blue-500 text-blue-900 font-medium' 
                                    : 'bg-gray-50 border-2 border-gray-200 text-gray-700 hover:bg-gray-100'
                                }
                            `}
                        >
                            {item}
                        </div>
                    )
                })}
            </div>
            {toAdd.length > 0 && 
                <div className="flex justify-end">
                    <Button 
                        className="mt-4"
                        onClick={addEmails}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Adding..." : `Add ${toAdd.length} to Whitelist`}
                    </Button>
                </div>
            }
        </CardContent>
    </Card>
}